import { heroBriefs, heroFeature, heroSideCards, siteStats } from '@/mocks/home';

const trendIcon = {
  up: 'ri-arrow-up-line text-primary-600',
  flat: 'ri-subtract-line text-foreground-400',
  down: 'ri-arrow-down-line text-secondary-600',
};

export default function HeroSection() {
  return (
    <section id="top" className="border-b border-background-200 bg-background-100">
      <div className="mx-auto max-w-[1400px] px-4 py-8 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-5">
          {/* Featured card */}
          <article className="group relative animate-rise overflow-hidden rounded-lg border border-background-300 lg:col-span-8">
            <div className="relative h-[280px] w-full md:h-[380px] lg:h-[460px]">
              <img
                src="https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photograph%20of%20an%20empty%20conference%20table%20with%20warm%20amber%20rim%20light%2C%20deep%20shadows%2C%20blurred%20night%20city%20bokeh%20in%20background%2C%20film%20grain%2C%20high%20contrast%2C%20atmospheric%2C%20no%20text&width=1200&height=800&seq=hl-hero-main&orientation=landscape"
                alt="黑料爆料事件始末专题配图"
                title="黑料爆料事件始末 全过程梳理"
                className="h-full w-full object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/10" />
            </div>

            <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
                  {heroFeature.tag}
                </span>
                {heroFeature.stages.map((stage, index) => (
                  <span key={stage} className="flex items-center gap-1 text-xs text-background-200">
                    {index > 0 ? <i className="ri-arrow-right-s-line text-sm" /> : null}
                    {stage}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-xl font-black leading-snug text-background-50 md:text-3xl">
                {heroFeature.title}
              </h1>
              <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-200 sm:block">
                {heroFeature.excerpt}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-background-300">
                <span className="flex items-center gap-1">
                  <i className="ri-user-star-line text-sm" />
                  {heroFeature.meta}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-fire-line text-sm text-accent-400" />
                  {heroFeature.views}
                </span>
                <a
                  href="#feed"
                  className="ml-auto inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-950 transition-colors hover:bg-background-200"
                >
                  查看全过程
                  <i className="ri-arrow-right-line text-base" />
                </a>
              </div>
            </div>
          </article>

          {/* Side stack */}
          <div className="flex flex-col gap-4 lg:col-span-4 lg:gap-5">
            {heroSideCards.map((card, index) => (
              <article
                key={card.title}
                className="group flex animate-rise cursor-pointer flex-col overflow-hidden rounded-lg border border-background-300 bg-background-50 transition-colors hover:border-primary-300"
                style={{ animationDelay: `${0.08 * (index + 1)}s` }}
              >
                <div className="relative h-32 w-full overflow-hidden md:h-36">
                  <img
                    src={card.image}
                    alt={card.title}
                    title={`${card.title} 黑料爆料`}
                    className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-2.5 py-1 text-[11px] font-bold text-primary-700">
                    {card.tag}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                    {card.title}
                  </h3>
                  <div className="mt-3 flex items-center justify-between text-xs text-foreground-500">
                    <span>{card.meta}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line text-sm" />
                      {card.views}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Brief strip */}
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {heroBriefs.map((brief) => (
            <a
              key={brief.rank}
              href="#rank"
              className="group flex cursor-pointer items-start gap-3 rounded-lg border border-background-300 bg-background-50 p-4 transition-colors hover:border-accent-300 hover:bg-accent-50"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-foreground-950 text-xs font-bold text-background-50">
                {brief.rank}
              </span>
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-sm font-semibold leading-snug text-foreground-900 group-hover:text-primary-700">
                  {brief.title}
                </p>
                <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
                  <span>{brief.source}</span>
                  <span className="flex items-center gap-0.5">
                    <i className={`text-sm ${trendIcon[brief.trend]}`} />
                    热度 {brief.heat}
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Stats */}
        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
          {siteStats.map((stat) => (
            <div key={stat.label} className="rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-center">
              <p className="font-heading text-xl font-black text-primary-600 md:text-2xl">{stat.value}</p>
              <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}